import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums, Tables } from "@how/database";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type Weekday = Enums<"weekday">;
export type GroupVisibility = Enums<"group_visibility">;
export type GroupJoiningRule = Enums<"group_joining_rule">;
export type GroupMemberRole = Enums<"group_member_role">;
export type GroupMembershipStatus = Enums<"group_membership_status">;

export const VISIBILITY_LABEL: Record<GroupVisibility, string> = {
  public: "Public",
  unlisted: "Unlisted",
  private: "Private",
};
export const JOINING_LABEL: Record<GroupJoiningRule, string> = {
  open: "Open",
  request: "By request",
  closed: "Closed",
};
export const DAY_LABEL: Record<Weekday, string> = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

/** "19:00" as "7:00 PM", in the reader's locale; the time is a wall-clock time at the campus (ADR 0036). */
export function formatMeetingTime(time: string | null): string | null {
  if (time === null) return null;
  const [h, m] = time.split(":");
  if (h === undefined || m === undefined) return time;
  const d = new Date(2000, 0, 1, Number(h), Number(m));
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export interface GroupCategory {
  readonly id: string;
  readonly name: string;
  readonly sortOrder: number;
}

export function useGroupCategories() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["group-categories", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<GroupCategory[]> => {
      const { data, error } = await supabase
        .from("group_categories")
        .select("id, name, sort_order")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((c) => ({ id: c.id, name: c.name, sortOrder: c.sort_order }));
    },
  });
}

export function useCampusOptions() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campus-options", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      const { data, error } = await supabase
        .from("campuses")
        .select("id, name")
        .eq("tenant_id", tenantId ?? "")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((c) => ({ value: c.id, label: c.name }));
    },
  });
}

/**
 * The groups the signed-in person leads. The policies show a leader every row of the groups they lead and their
 * own rows elsewhere, and nothing else, so the leader rows they can see name exactly the groups they lead. The
 * people table is not consulted: a group leader's role has no people.read (ADR 0040).
 */
export function useMyLedGroupIds() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-led-groups", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("group_memberships")
        .select("group_seasons(group_id)")
        .eq("tenant_id", tenantId ?? "")
        .eq("role", "leader")
        .eq("status", "active");
      if (error) throw error;
      return [...new Set(data.map((row) => row.group_seasons.group_id))];
    },
  });
}

export interface GroupRow {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string | null;
  readonly categoryId: string | null;
  readonly categoryName: string | null;
  readonly campusId: string | null;
  readonly campusName: string | null;
  readonly meetingDay: Weekday | null;
  readonly meetingTime: string | null;
  readonly meetingLocation: string | null;
  readonly visibility: GroupVisibility;
  readonly joiningRule: GroupJoiningRule;
  readonly capacity: number | null;
  readonly ageMin: number | null;
  readonly ageMax: number | null;
  readonly membersConfidential: boolean;
  readonly isActive: boolean;
  readonly activeMembers: number;
  readonly pendingRequests: number;
  readonly leaders: readonly string[];
}

export interface GroupsQuery {
  readonly search: string;
  readonly categoryId?: string;
  readonly campusId?: string;
  readonly status: "active" | "archived" | "all";
  /** When set, only these groups are listed: a leader without groups.read sees the ones they lead. */
  readonly onlyIds?: readonly string[];
}

/** A church has tens of groups, so the list is read whole; the counts come from one definer function. */
export function useGroups(q: GroupsQuery) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["groups", tenantId, q],
    enabled: tenantId !== null,
    queryFn: async (): Promise<GroupRow[]> => {
      let query = supabase
        .from("groups")
        .select(
          "id, name, slug, description, category_id, campus_id, meeting_day, meeting_time, meeting_location, visibility, joining_rule, capacity, age_min, age_max, members_confidential, is_active, group_categories(name), campuses(name)",
        )
        .eq("tenant_id", tenantId ?? "")
        .order("name");
      if (q.status !== "all") query = query.eq("is_active", q.status === "active");
      if (q.categoryId !== undefined) query = query.eq("category_id", q.categoryId);
      if (q.campusId !== undefined) query = query.eq("campus_id", q.campusId);
      if (q.onlyIds !== undefined) query = query.in("id", [...q.onlyIds]);
      const trimmed = q.search.trim().replace(/[%_]/g, "");
      if (trimmed.length > 0) query = query.ilike("name", `%${trimmed}%`);
      const [groups, counts] = await Promise.all([
        query,
        supabase.rpc("group_counts", { p_tenant_id: tenantId ?? "" }),
      ]);
      if (groups.error) throw groups.error;
      if (counts.error) throw counts.error;
      const byGroup = new Map(counts.data.map((c) => [c.group_id, c]));
      return groups.data.map((g) => {
        const c = byGroup.get(g.id);
        return {
          id: g.id,
          name: g.name,
          slug: g.slug,
          description: g.description,
          categoryId: g.category_id,
          categoryName: g.group_categories?.name ?? null,
          campusId: g.campus_id,
          campusName: g.campuses?.name ?? null,
          meetingDay: g.meeting_day,
          meetingTime: g.meeting_time,
          meetingLocation: g.meeting_location,
          visibility: g.visibility,
          joiningRule: g.joining_rule,
          capacity: g.capacity,
          ageMin: g.age_min,
          ageMax: g.age_max,
          membersConfidential: g.members_confidential,
          isActive: g.is_active,
          activeMembers: c?.active_members ?? 0,
          pendingRequests: c?.pending_requests ?? 0,
          leaders: c?.leaders ?? [],
        };
      });
    },
  });
}

export interface GroupSeason {
  readonly id: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly isCurrent: boolean;
}

export function useGroupSeasons(groupId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["group-seasons", tenantId, groupId],
    enabled: groupId !== null,
    queryFn: async (): Promise<GroupSeason[]> => {
      const { data, error } = await supabase
        .from("group_seasons")
        .select("id, name, starts_on, ends_on, is_current")
        .eq("group_id", groupId ?? "")
        .order("starts_on", { ascending: false });
      if (error) throw error;
      return data.map((s) => ({
        id: s.id,
        name: s.name,
        startsOn: s.starts_on,
        endsOn: s.ends_on,
        isCurrent: s.is_current,
      }));
    },
  });
}

const rosterMemberSchema = z.object({
  membershipId: z.string(),
  personId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  role: z.enum(["member", "leader"]),
  status: z.enum(["requested", "active", "left", "declined"]),
  requestedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  leftAt: z.string().nullable(),
  /** What the member wrote when asking to join (ADR 0040, owner decision 1). Absent before task 5's migration. */
  requestNote: z.string().nullable().optional(),
});
const rosterSchema = z.object({
  season: z.object({
    id: z.string(),
    name: z.string(),
    startsOn: z.string(),
    endsOn: z.string().nullable(),
    isCurrent: z.boolean(),
  }),
  members: z.array(rosterMemberSchema),
  childrenHidden: z.number(),
});
export type RosterMember = z.infer<typeof rosterMemberSchema>;
export type Roster = z.infer<typeof rosterSchema>;

export function memberName(m: RosterMember): string {
  return `${m.preferredName ?? m.firstName} ${m.lastName}`;
}

/** The roster with names, through group_roster: staff and the group's leaders, under ADR 0028's rule. */
export function useGroupRoster(groupId: string | null, seasonId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["group-roster", tenantId, groupId, seasonId],
    enabled: groupId !== null,
    retry: false,
    queryFn: async (): Promise<Roster> => {
      const { data, error } = await supabase.rpc("group_roster", {
        p_group_id: groupId ?? "",
        ...(seasonId === null ? {} : { p_season_id: seasonId }),
      });
      if (error) throw error;
      return rosterSchema.parse(data);
    },
  });
}

function useGroupInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (groupId?: string) => {
    await client.invalidateQueries({ queryKey: ["groups", tenantId] });
    await client.invalidateQueries({ queryKey: ["group-categories", tenantId] });
    await client.invalidateQueries({ queryKey: ["my-led-groups", tenantId] });
    // A roster change is on the person's timeline too.
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    if (groupId !== undefined) {
      await client.invalidateQueries({ queryKey: ["group-seasons", tenantId, groupId] });
      await client.invalidateQueries({ queryKey: ["group-roster", tenantId, groupId] });
    }
  };
}

export interface GroupFields {
  readonly name: string;
  readonly slug: string;
  readonly description: string;
  readonly categoryId: string | null;
  readonly campusId: string | null;
  readonly meetingDay: Weekday | null;
  readonly meetingTime: string;
  readonly meetingLocation: string;
  readonly visibility: GroupVisibility;
  readonly joiningRule: GroupJoiningRule;
  readonly capacity: number | null;
  readonly ageMin: number | null;
  readonly ageMax: number | null;
  /** Members see only the leaders (ADR 0040 decision 5). */
  readonly membersConfidential: boolean;
}

/** Create (groupId null) or fully replace a group: set_group takes every field each time. */
export function useSaveGroup() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string | null; fields: GroupFields }) => {
      const f = input.fields;
      const { data, error } = await supabase.rpc("set_group", {
        p_tenant_id: tenantId ?? "",
        p_name: f.name,
        ...(input.groupId === null ? {} : { p_group_id: input.groupId }),
        ...(f.slug.trim() === "" ? {} : { p_slug: f.slug.trim() }),
        ...(f.description.trim() === "" ? {} : { p_description: f.description }),
        ...(f.categoryId === null ? {} : { p_category_id: f.categoryId }),
        ...(f.campusId === null ? {} : { p_campus_id: f.campusId }),
        ...(f.meetingDay === null ? {} : { p_meeting_day: f.meetingDay }),
        ...(f.meetingTime.trim() === "" ? {} : { p_meeting_time: f.meetingTime }),
        ...(f.meetingLocation.trim() === "" ? {} : { p_meeting_location: f.meetingLocation }),
        p_visibility: f.visibility,
        p_joining_rule: f.joiningRule,
        ...(f.capacity === null ? {} : { p_capacity: f.capacity }),
        ...(f.ageMin === null ? {} : { p_age_min: f.ageMin }),
        ...(f.ageMax === null ? {} : { p_age_max: f.ageMax }),
        p_members_confidential: f.membersConfidential,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_id, input) => invalidate(input.groupId ?? undefined),
  });
}

export function useSetGroupActive() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string; isActive: boolean }) => {
      const { error } = await supabase.rpc("set_group_active", {
        p_group_id: input.groupId,
        p_is_active: input.isActive,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.groupId),
  });
}

export interface SeasonFields {
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly isCurrent: boolean;
}

export function useSaveSeason() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: {
      groupId: string;
      seasonId: string | null;
      fields: SeasonFields;
    }) => {
      const { data, error } = await supabase.rpc("set_group_season", {
        p_group_id: input.groupId,
        p_name: input.fields.name,
        ...(input.seasonId === null ? {} : { p_season_id: input.seasonId }),
        p_starts_on: input.fields.startsOn,
        ...(input.fields.endsOn === "" ? {} : { p_ends_on: input.fields.endsOn }),
        p_is_current: input.fields.isCurrent,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_id, input) => invalidate(input.groupId),
  });
}

export function useAddGroupMember() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: {
      groupId: string;
      seasonId: string;
      personId: string;
      role: GroupMemberRole;
    }) => {
      const { error } = await supabase.rpc("add_group_member", {
        p_season_id: input.seasonId,
        p_person_id: input.personId,
        p_role: input.role,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.groupId),
  });
}

export function useSetGroupMemberRole() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string; membershipId: string; role: GroupMemberRole }) => {
      const { error } = await supabase.rpc("set_group_member_role", {
        p_membership_id: input.membershipId,
        p_role: input.role,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.groupId),
  });
}

export function useRemoveGroupMember() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string; membershipId: string }) => {
      const { error } = await supabase.rpc("remove_group_member", {
        p_membership_id: input.membershipId,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.groupId),
  });
}

export function useDecideGroupRequest() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string; membershipId: string; approve: boolean }) => {
      const { error } = await supabase.rpc("decide_group_request", {
        p_membership_id: input.membershipId,
        p_approve: input.approve,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.groupId),
  });
}

export function useSaveCategory() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (input: { categoryId: string | null; name: string; sortOrder: number }) => {
      const { error } = await supabase.rpc("set_group_category", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        ...(input.categoryId === null ? {} : { p_category_id: input.categoryId }),
        p_sort_order: input.sortOrder,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useDeleteCategory() {
  const supabase = useSupabase();
  const invalidate = useGroupInvalidation();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase.rpc("delete_group_category", { p_category_id: categoryId });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

/** Only the columns a picker needs; the same shape the household picker uses. */
export type PersonOption = { value: string; label: string };
export type GroupRowLike = Pick<Tables<"groups">, "id" | "name">;
